import { VoyageRoute, WeatherLimits, VoyageRouteLineFeature, VoyageRoutePointFeature, VoyageRoutingModel, WeatherLimitPeriodValue, WeatherSummary, WeatherWarning, WarningSegment } from "../models/voyage-routing-model";
import { LocationAndTime, WeatherModel } from "../models/weather-model";
import { getLineStringForFractionPositions, getHeadingForPoint } from "./map-utils";
import { CoordinatesUtils } from "./coordinates-utils";
import { Coordinate } from "ol/coordinate";
import { findLastIndex } from "./helpers";

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

interface SpotWeatherInfo
{
    element: string;
    value: number[];
}

interface SimpleVector
{
    x: number;
    y: number;
}

interface ExceededWeatherLimits
{
    warn: Map<keyof WeatherLimits, WeatherWarning>;
    avoid: Map<keyof WeatherLimits, WeatherWarning>;
}

export interface WarningPoint
{
	coordinates: [number,number]; 
	timestamp: string;
	warnings: ExceededWeatherLimits;
}

export interface SpotWeatherWeather {
	value: number,
	direction?: number
}

export interface SpotWeather {
	coordinates: [number, number],
	timestamp: string
	weather: {
        [index: string]: SpotWeatherWeather
    }
}

export class RouteWeatherWorker
{
    constructor(route: VoyageRoute, weatherLimits: WeatherLimits, failOnAvoidLimitExceeded?: boolean)
    {
        this._route = route;
        this._weatherLimits = weatherLimits;
        if (failOnAvoidLimitExceeded)
        {
            this._failOnAvoidLimitExceeded = true;
        }
    }

    async processRoute(): Promise<void>
    {
        // Add weather information to route and compare with weather limits
        const timeStart = Date.now();

        // Collect all weather locations+times
        const lineStrings: VoyageRouteLineFeature[] = [];
        const locations: LocationAndTime[] = [];
        let previousPoint: VoyageRoutePointFeature|null = null;

        // Skip past passed waypoints
        const lastIsPassed = findLastIndex(this._route.route_geometry, f => f.geometry.type === "Point" && f.properties.isPassed)
        if (lastIsPassed !== -1)
        {
            previousPoint = <VoyageRoutePointFeature>this._route.route_geometry[lastIsPassed];
        }

        for (let i = lastIsPassed+1; i < this._route.route_geometry.length; i++)
        {
            const feature = this._route.route_geometry[i];
            if (feature.geometry.type === "LineString")
            {
                const lineEndPoint = <VoyageRoutePointFeature>this._route.route_geometry[i+1];
                const infoWaypoints = (<VoyageRouteLineFeature>feature).properties.informationalWaypoints;

                if (infoWaypoints)
                {
                    for (let j = 0; j < infoWaypoints.length; j++)
                    {
                        const endPoint = j === infoWaypoints.length-1 ? lineEndPoint : infoWaypoints[j+1];
                        const start: LocationAndTime = {
                            timestamp: infoWaypoints[j].properties.timestamp!,
                            coordinates: infoWaypoints[j].geometry.coordinates
                        };
                        const end: LocationAndTime = {
                            timestamp: endPoint.properties.timestamp!,
                            coordinates: endPoint.geometry.coordinates
                        }
                        locations.push(...this._getWeatherPointsBetween(start, end, feature.properties.leg_type));
                    }
                }
                else
                {
                    const lineStartPoint = <VoyageRoutePointFeature>this._route.route_geometry[i-1];
                    if (!lineStartPoint.properties.timestamp) {
                        // No timestamp information available - skip past this line string
                        continue;
                    }
                    const start: LocationAndTime = {
                        timestamp: lineStartPoint.properties.timestamp,
                        coordinates: lineStartPoint.geometry.coordinates
                    };
                    const end: LocationAndTime = {
                        timestamp: lineEndPoint.properties.timestamp!,
                        coordinates: lineEndPoint.geometry.coordinates
                    }
                    locations.push(...this._getWeatherPointsBetween(start, end, feature.properties.leg_type));
                }

                lineStrings.push(<VoyageRouteLineFeature>feature);
            }

            if (feature.geometry.type === "Point" && i === this._route.route_geometry.length-1)
            {
                // Add last point
                locations.push({
                    timestamp: feature.properties.timestamp!,
                    coordinates: feature.geometry.coordinates
                })
            }
        }

        let weatherPoints: any[] = [];

         if(!isVesselOnCloud) {
            try {
                weatherPoints = await WeatherModel!.getMultiSpotWeather(locations);
            } catch (err) {
                console.warn('Failed to fetch weather data, route will be displayed without weather information:', err);
            }

        // Only process weather if we have valid data
        if (weatherPoints && weatherPoints.length > 0) {
            const weatherWorkers: Promise<void>[] = [];
            for (const lineString of lineStrings)
            {
                const pointCompare = (coord : Coordinate, point: VoyageRoutePointFeature) => {
                    return (Math.abs(coord[0] - point.geometry.coordinates[0]) < 0.0001) &&
                           (Math.abs(coord[1] - point.geometry.coordinates[1]) < 0.0001);
                };

                const weatherStart = weatherPoints.findIndex( point => pointCompare(lineString.geometry.coordinates[0], point) );
                const weatherEnd = weatherPoints.findIndex( point => pointCompare(lineString.geometry.coordinates[1], point) );

                weatherWorkers.push(this._addWeatherToLineString(lineString, weatherPoints.slice(weatherStart,weatherEnd+1)));
            }

            await Promise.all(weatherWorkers);
        } else {
            console.warn('No weather data available, route will be displayed without weather information');
        }
    }

        // Add weather summary for entire route
        // Note: Average values are just the simple average of the average values from the linestrings for now
        // (ie. a long linestring has the same weight as a short) - if we decide to actually use it for
        // something we should probably calculate a more accurate value
        const weatherSummary: { [index:string]: WeatherSummary } = {};
        const averages: { [index:string]: { count:number, sum: number } } = {};
        for (const lineString of lineStrings)
        {
            for (const weatherType in lineString.properties.weather)
            {
                if (!weatherSummary[weatherType])
                {
                    weatherSummary[weatherType] = {
                        max: { value: lineString.properties.weather[weatherType].max.value },
                        min: { value: lineString.properties.weather[weatherType].min.value },
                        average: { value: lineString.properties.weather[weatherType].average.value } // Overwritten later
                    };
                    averages[weatherType] = {
                        count: 1,
                        sum: lineString.properties.weather[weatherType].average.value
                    };
                }
                else
                {
                    if (lineString.properties.weather[weatherType].max.value > weatherSummary[weatherType].max.value)
                    {
                        weatherSummary[weatherType].max = { value: lineString.properties.weather[weatherType].max.value };
                    }
                    if (lineString.properties.weather[weatherType].min.value < weatherSummary[weatherType].min.value)
                    {
                        weatherSummary[weatherType].min = { value: lineString.properties.weather[weatherType].min.value };
                    }
                    averages[weatherType].count++;
                    averages[weatherType].sum += lineString.properties.weather[weatherType].average.value;
                }
            }
        }

        // Calculate resulting averages
        for (const weatherType in averages)
        {
            weatherSummary[weatherType].average.value = averages[weatherType].sum/averages[weatherType].count;
        }

        const forecastInfo = WeatherModel!.latestForecastInfo;

        // Only set weather info if forecast info is available
        if (forecastInfo) {
            this._route.route_info!.weather.forecast_versions = [WeatherModel!.latestForecastVersion];
            this._route.route_info!.weather.subscriptions = [];
            this._route.route_info!.weather.hasLowResData = forecastInfo.hasLowResData;
            this._route.route_info!.weather.hasHighResData = forecastInfo.hasHighResData;
            this._route.route_info!.weather.weatherPoints = weatherPoints;
        } else {
            console.warn('No forecast info available');
            this._route.route_info!.weather.forecast_versions = [];
            this._route.route_info!.weather.subscriptions = [];
            this._route.route_info!.weather.weatherPoints = weatherPoints;
        }

        if (forecastInfo && forecastInfo.subscriptions)
        {
            for (const subInfo of forecastInfo.subscriptions)
            {
                this._route.route_info!.weather.subscriptions.push({
                    forecast: forecastInfo.name,
                    id: subInfo.id,
                    importedAt: subInfo.import_date
                });
            }
        }
        this._route.route_info!.weather.summary = weatherSummary;
        this._route.route_info!.calculationInput.weatherLimits = this._weatherLimits;
        if (forecastInfo) {
            this._route.route_info!.calculationInput.forecast_versions = [WeatherModel!.latestForecastVersion];
        } else {
            this._route.route_info!.calculationInput.forecast_versions = [];
        }

        console.log("Weather enriched route in ", Date.now() - timeStart);
    }

    private async _addWeatherToLineString(line: VoyageRouteLineFeature, weatherPoints: VoyageRoutePointFeature[]): Promise<void>
    {
        const weatherSum: { [index:string]: WeatherSummary } = {};
        const warningPoints: WarningPoint[] = []; // for creating warning segments (warn and avoid)
        const spotData: SpotWeather[] = []; // weather data for graphs

        // Note: For average values we use a vector for the direction, and a simple average for magnitude
        const averages: { [index:string]: { value: { count:number, sum: number }, direction?: SimpleVector } } = {};
        for (let i = 0; i < weatherPoints.length; i++)
        {
            const point = weatherPoints[i];

            let spot: SpotWeather|undefined;
            if (point.properties.timestamp) {
                spot = {
                    coordinates: point.geometry.coordinates,
                    timestamp: point.properties.timestamp,
                    weather: {}
                }
            }

            for (const weather of point.properties.weather)
            {
                // Note: Weather values are an array of either [direction, value] or [value] (depending on type)
                const value = weather.value[weather.value.length-1];
                const direction = weather.value.length > 1 ? weather.value[0] : undefined;

                if (!weatherSum[weather.element])
                {
                    weatherSum[weather.element] = {
                        max: { value, direction },
                        min: { value, direction },
                        average: { value, direction } // Overwritten later
                    };
                    averages[weather.element] = {
                        value: {
                            count: 1,
                            sum: value
                        }
                    };
                    if (direction !== undefined) {
                        averages[weather.element].direction = this._getVectorFromDirection(direction);
                    }
                }
                else
                {
                    if (value > weatherSum[weather.element].max.value) {
                        weatherSum[weather.element].max = { value, direction };
                    }
                    if (value < weatherSum[weather.element].min.value) {
                        weatherSum[weather.element].min = { value, direction };
                    }
                    averages[weather.element].value.count++;
                    averages[weather.element].value.sum += value;
                    if (direction !== undefined) {
                        const vector = this._getVectorFromDirection(direction);
                        averages[weather.element].direction!.x += vector.x;
                        averages[weather.element].direction!.y += vector.y;
                    }
                }

                if (spot) {
                    if (direction !== undefined) {
                        spot.weather[weather.element] = {direction, value} 
                    } else {
                        spot.weather[weather.element] = {value} 
                    }
                }
            }
            line.properties.spotWeathers = spotData;

            // Check against weather limits
            let course = line.properties.course;
            if (course === undefined)
            {
                const lineString = getLineStringForFractionPositions(line);
                course = getHeadingForPoint(weatherPoints[i].geometry.coordinates, lineString);

            } 
            const exceededLimits = this._checkWeatherAgainstLimits(point.properties.weather, course);

            if (this._failOnAvoidLimitExceeded && Array.from(exceededLimits.avoid.values()).length > 0)
            {
                const avoidName = Array.from(exceededLimits.avoid.values())[0].type;
                const degrees = CoordinatesUtils.decimalToDegreesAndMinutes(point.geometry.coordinates);
                throw new Error(`Avoid weather limit for ${avoidName} exceeded at ${degrees.lat}, ${degrees.lon}`);
            }

            warningPoints.push({
                coordinates: point.geometry.coordinates, 
                timestamp: point.properties.timestamp!,
                warnings: exceededLimits
            });
            if (spot) {
                spotData.push(spot);
            }
        }

        // Add averages
        for (const weatherName in averages)
        {
            weatherSum[weatherName].average.value = averages[weatherName].value.sum/averages[weatherName].value.count;
            if (averages[weatherName].direction)
            {
                weatherSum[weatherName].average.direction = this._getAngleFromVector(averages[weatherName].direction!);
            }
        }

        line.properties.weather = weatherSum;

        if (!line.properties.warnings)
        {
            line.properties.warnings = {
                weather: { avoid:[], warn:[] }
            }
        }
        line.properties.warnings.weather = this._getWarningsFromWarningPoints(warningPoints);
    }


    private _route: VoyageRoute;
    private _weatherLimits: WeatherLimits;
    private _failOnAvoidLimitExceeded: boolean = false;

    // Returns locations for spot weather lookups between two points
    // Note: Will include the start point, but not end point
    private _getWeatherPointsBetween(start: LocationAndTime, end: LocationAndTime, legType: "Rhumbline"|"GreatCircle"): LocationAndTime[]
    {
        const locations: LocationAndTime[] = [];

        // Start with the start point
        locations.push(start);

        const lineString = getLineStringForFractionPositions({
            type: "Feature",
            geometry: { type: "LineString", coordinates: [ start.coordinates, end.coordinates ] },
            properties: { leg_type: legType }
        });

        const weatherInterval = 10*60*1000; // Lookup weather in 10 minutes intervals
        let timestamp = new Date(start.timestamp).getTime();
        const startTime = new Date(start.timestamp).getTime();
        const endTime = new Date(end.timestamp).getTime();
        while (timestamp + weatherInterval < endTime)
        {
            timestamp += weatherInterval;
            locations.push({
                timestamp: new Date(timestamp).toISOString(),
                coordinates: <[number, number]>lineString.getCoordinateAt((timestamp-startTime)/(endTime-startTime))
            });
        }

        return locations;
    }

    private _checkWeatherAgainstLimits(weather: SpotWeatherInfo[], course: number): ExceededWeatherLimits
    {
        const output: ExceededWeatherLimits = {
            warn: new Map(),
            avoid: new Map()
        };

        let limit: keyof WeatherLimits;
        for (limit in this._weatherLimits)
        {
            if (limit.endsWith("Period"))
            {
                // Will be handled by the associated limit
                continue;
            }

            if (this._weatherLimits[limit].warn !== null || this._weatherLimits[limit].avoid !== null)
            {
                switch (limit)
                {
                    case "Waves":
                    {
                        if (this._weatherLimits.Waves.warn || this._weatherLimits.Waves.avoid)
                        {
                            const wave = weather.find(e => e.element === "Wave");
                            const value = wave ? wave.value[1] : 0;
                            if (this._weatherLimits.Waves.warn && (value > this._weatherLimits.Waves.warn))
                            {
                                this._addExceedingLimit(output.warn, {
                                    type: limit,
                                    actual: value,
                                    limit: this._weatherLimits.Waves.warn
                                });
                            }
                            if (this._weatherLimits.Waves.avoid && (value > this._weatherLimits.Waves.avoid))
                            {
                                this._addExceedingLimit(output.avoid, {
                                    type: limit,
                                    actual: value,
                                    limit: this._weatherLimits.Waves.avoid
                                });
                            }
                        }
                    }
                    break;
                    
                    case "HeadWind":
                    {
                        // We have head wind if the ship is hit in direction -45->45 (or 0->45 and 315->360) degrees
                        this._checkWindLimit(limit, weather, [[0, 45], [315, 360]], course, output);
                    }
                    break;
                    
                    case "BeamWind":
                    {
                        // We have beam wind if the ship is hit in direction 45->135 and 225->315 degrees
                        this._checkWindLimit(limit, weather, [[45, 135], [225, 315]], course, output);
                    }
                    break;
    
                    case "FollowingWind":
                    {
                        // We have following wind if the ship is hit in direction 135->225 degrees
                        this._checkWindLimit(limit, weather, [[135, 225]], course, output);
                    }
                    break;
    
                    case "BeamWavesHeight":
                    {
                        // We have beam waves if the ship is hit in directions 45->135 and 225->315 degrees
                        this._checkWaveLimit(limit, weather, [[45, 135], [225, 315]], course, output);
                    }
                    break;
    
                    case "HeadWavesHeight":
                    {
                        // We have head waves if the ship is hit in direction -45->45 (or 0->45 and 315->360) degrees
                        this._checkWaveLimit(limit, weather, [[0, 45], [315, 360]], course, output);
                    }
                    break;
    
                    case "FollowingWavesHeight":
                    {
                        // We have following waves if the ship is hit in direction 135->225 degrees
                        this._checkWaveLimit(limit, weather, [[135, 225]], course, output);
                    }
                    break;
    
                    default:
                    {
                        // The rest are the simple cases where the limit name is the same as the weather (Wind10M etc)
                        // All of these limits are maximum values
                        // The weather values are either [direction, value] or [value] depending on type
                        const weatherInfo = weather.find(e => e.element === limit);
                        const warnLimit = <number|null>this._weatherLimits[limit].warn;
                        const avoidLimit = <number|null>this._weatherLimits[limit].avoid;
                        if (warnLimit !== null && weatherInfo && weatherInfo.value[weatherInfo.value.length - 1] >= warnLimit)
                        {
                            this._addExceedingLimit(output.warn, {
                                type: limit,
                                actual: weatherInfo.value[weatherInfo.value.length - 1],
                                limit: warnLimit
                            });
                        }

                        if (avoidLimit !== null && weatherInfo && weatherInfo.value[weatherInfo.value.length - 1] >= avoidLimit)
                        {
                            this._addExceedingLimit(output.avoid, {
                                type: limit,
                                actual: weatherInfo.value[weatherInfo.value.length - 1],
                                limit: avoidLimit
                            });
                        }
                    }
                    break;
                }
            }
        }

        return output;
    }

    private _addExceedingLimit(map: Map<keyof WeatherLimits, WeatherWarning>, newEntry: WeatherWarning)
    {
        const currentEntry = map.get(newEntry.type);
        if (currentEntry)
        {
            // The actual (weather) value may be higher than the current entry - always show the maximum weather value encountered
            currentEntry.actual = Math.max(currentEntry.actual!, newEntry.actual!);
        }
        else
        {
            map.set(newEntry.type, newEntry);
        }
    }

    private _checkWindLimit(type: keyof WeatherLimits, weather: SpotWeatherInfo[], directionLimits: Array<[number, number]>,
                               course: number, output: ExceededWeatherLimits): void
    {
        // Look at Wind10M and whether the direction matches
        const wind10M = weather.find( e => e.element === "Wind10M" );
            
        if (wind10M)
        {
            const direction = this._getWeatherDirection(course, wind10M.value[0]);
            let directionMatch = false;
            for (const directionLimit of directionLimits)
            {
                if (direction >= directionLimit[0] && direction <= directionLimit[1])
                {
                    directionMatch = true;
                    break;
                }
            }

            if (directionMatch)
            {
                if (this._weatherLimits[type].warn && wind10M.value[1] > this._weatherLimits[type].warn!)
                {
                    this._addExceedingLimit(output.warn, {
                        type: type,
                        actual: wind10M.value[1],
                        limit: <number>this._weatherLimits[type].warn!
                    });
                }
                if (this._weatherLimits[type].avoid && wind10M.value[1] > this._weatherLimits[type].avoid!)
                {
                    this._addExceedingLimit(output.avoid, {
                        type: type,
                        actual: wind10M.value[1],
                        limit: <number>this._weatherLimits[type].avoid!
                    });
                }
            }
        }
    }

    private _checkWaveLimit(type: keyof WeatherLimits, weather: SpotWeatherInfo[], directionLimits: Array<[number, number]>,
                            course: number, output: ExceededWeatherLimits): void
    {
        // Need to look at both Swell and Wave weather elements (as well as their periods) and whether the direction matches
        const limitToPeriod: {[index: string]: keyof WeatherLimits} = {
            BeamWavesHeight: "BeamWavesPeriod", HeadWavesHeight: "HeadWavesPeriod", FollowingWavesHeight: "FollowingWavesPeriod"
        };

        const weatherMatchesAndExceedsLimit = (weatherValue: SpotWeatherInfo, weatherPeriod: SpotWeatherInfo,
                                               value: number, period: { max: number|null, min: number|null }) => {
            const direction = this._getWeatherDirection(course, weatherValue.value[0]);
            let directionMatch = false;
            for (const directionLimit of directionLimits)
            {
                if (direction >= directionLimit[0] && direction <= directionLimit[1])
                {
                    directionMatch = true;
                    break;
                }
            }
            
            return (directionMatch && weatherValue.value[1] > value &&
                    weatherPeriod.value[0] >= period.min! && weatherPeriod.value[0] <= period.max!);
        };
    
        const wave = weather.find( e => e.element === "Wave" );
        const wavePeriod = weather.find( e => e.element === "WavePeriod" );
        const swell = weather.find( e => e.element === "Swell" );
        const swellPeriod = weather.find( e => e.element === "SwellPeriod" );

        if (wave && wavePeriod)
        {
            let value = <number>this._weatherLimits[type].warn;
            let period = (<WeatherLimitPeriodValue>this._weatherLimits[limitToPeriod[type]]).warn;
            if (weatherMatchesAndExceedsLimit(wave, wavePeriod, value, period))
            {
                this._addExceedingLimit(output.warn, {
                    type: type,
                    actual: wave.value[1],
                    limit: value
                });
            }

            value = <number>this._weatherLimits[type].avoid;
            period = (<WeatherLimitPeriodValue>this._weatherLimits[limitToPeriod[type]]).avoid;
            if (weatherMatchesAndExceedsLimit(wave, wavePeriod, value, period))
            {
                this._addExceedingLimit(output.avoid, {
                    type: type,
                    actual: wave.value[1],
                    limit: value
                });
            }
        }

        if (swell && swellPeriod)
        {
            let value = <number>this._weatherLimits[type].warn;
            let period = (<WeatherLimitPeriodValue>this._weatherLimits[limitToPeriod[type]]).warn;
            if (weatherMatchesAndExceedsLimit(swell, swellPeriod, value, period))
            {
                this._addExceedingLimit(output.warn, {
                    type: type,
                    actual: swell.value[1],
                    limit: value
                });
            }

            value = <number>this._weatherLimits[type].avoid;
            period = (<WeatherLimitPeriodValue>this._weatherLimits[limitToPeriod[type]]).avoid;
            if (weatherMatchesAndExceedsLimit(swell, swellPeriod, value, period))
            {
                this._addExceedingLimit(output.avoid, {
                    type: type,
                    actual: swell.value[1],
                    limit: value
                });
            }
        }
    }

    private _getAngleFromVector(vector: SimpleVector)
    {
        let angle = Math.atan2(vector.x, vector.y)/(Math.PI/180);

        // Convert angle from [-180,180] to [0,360]
        return angle + 180;
    }

    private _getVectorFromDirection(direction: number): SimpleVector
    {
        // Convert direction from [0,360] to [-180,180]
        let angle = direction - 180;
        return {
            x: Math.sin(angle*(Math.PI/180)),
            y: Math.cos(angle*(Math.PI/180))
        }
    }

    private _getWeatherDirection(course: number, weatherHeading: number): number
    {
        if (course > weatherHeading)
        {
            return weatherHeading - (course - 360);
        }
        else
        {
            return weatherHeading - course;
        }
    }

    private _getWarningsFromWarningPoints(warningPoints: WarningPoint[]): { warn: WeatherWarning[]; avoid: WeatherWarning[]; }
    {
        const warns: Map<keyof WeatherLimits,WeatherWarning> = new Map();
        const avoids: Map<keyof WeatherLimits,WeatherWarning> = new Map();

        const ongoingWarns: Map<keyof WeatherLimits,{startidx:number,actual:number}> = new Map();
        const ongoingAvoids: Map<keyof WeatherLimits,{startidx:number,actual:number}> = new Map();

        for (let i = 0; i < warningPoints.length; i++)
        {
            const point = warningPoints[i];

            // Check current warns/avoids
            for (const [key, value] of ongoingWarns)
            {
                const pointWarn = point.warnings.warn.get(key);
                
                if (!pointWarn || value.actual !== pointWarn.actual || i === warningPoints.length - 1)
                {
                    // Warning has ended or actual has changed, record segment
                    const { newWarnStartIdx, newSegment } = this._generateWarningSegment(value.actual, warningPoints, pointWarn, value.startidx, i);
                    
                    if (newSegment)
                    {
                        let warning = warns.get(key);
                        if (!warning)
                        {
                            warning = {
                                type: key,
                                limit: warningPoints[value.startidx].warnings.warn.get(key)!.limit,
                                segments: [newSegment]
                            };
                        }
                        else
                        {
                            warning.segments!.push(newSegment);
                        }
                        warns.set(key, warning);
                    }

                    // Start new segment if needed or simply end the current one
                    if (newWarnStartIdx !== -1)
                    {
                        ongoingWarns.set(key, {
                            startidx: newWarnStartIdx,
                            actual: pointWarn!.actual!
                        });
                    }
                    else
                    {
                        ongoingWarns.delete(key);
                    }
                }
            }

            for (const [key, value] of ongoingAvoids)
            {
                const pointAvoid = point.warnings.avoid.get(key);
                
                if (!pointAvoid || value.actual !== pointAvoid.actual || i === warningPoints.length - 1)
                {
                    // Warning has ended or actual has changed, record segment
                    const { newWarnStartIdx, newSegment } = this._generateWarningSegment(value.actual, warningPoints, pointAvoid, value.startidx, i);
                    
                    if (newSegment)
                    {
                        // At the transition between an avoid (red) and warn (yellow) point warnings,
                        // we don't know how much of this distance is attributed to these two warnings respectively.
                        // It's better to stay safe and show worst case scenario, so let 
                        // avoid (red) take precedence if it is conflicting with a warn (yellow)
                        if (value.startidx > 0) {
                            const precedingWarn = warningPoints[value.startidx-1]?.warnings.warn.size;
                            const precedingAvoid = warningPoints[value.startidx-1]?.warnings.avoid.size;
                            if (precedingWarn && !precedingAvoid) {
                                newSegment.coordinates[0] = warningPoints[value.startidx-1].coordinates;
                            }
                        }
                        
                        // i is the first warningPoint in the next segment
                        if (i < warningPoints.length-1) {
                            const succedingWarn = warningPoints[i+1]?.warnings.warn.size;
                            const succedingAvoid = warningPoints[i+1]?.warnings.avoid.size;
                            if (succedingWarn && !succedingAvoid) {
                                newSegment.coordinates[1] = warningPoints[i].coordinates;
                            }
                        }

                        let warning = avoids.get(key);
                        if (!warning)
                        {
                            warning = {
                                type: key,
                                limit: warningPoints[value.startidx].warnings.avoid.get(key)!.limit,
                                segments: [newSegment]
                            };
                        }
                        else
                        {
                            warning.segments!.push(newSegment);
                        }
                        avoids.set(key, warning);
                    }

                    // Start new segment if needed or simply end the current one
                    if (newWarnStartIdx !== -1)
                    {
                        ongoingAvoids.set(key, {
                            startidx: newWarnStartIdx,
                            actual: pointAvoid!.actual!
                        });
                    }
                    else
                    {
                        ongoingAvoids.delete(key);
                    }
                }
            }

            // Check for new warns/avoids
            for (const [key, value] of point.warnings.warn)
            {
                if (!ongoingWarns.has(key))
                {
                    ongoingWarns.set(key, {
                        startidx: i,
                        actual: value.actual!
                    });
                }
            }

            for (const [key, value] of point.warnings.avoid)
            {
                if (!ongoingAvoids.has(key))
                {
                    ongoingAvoids.set(key, {
                        startidx: i,
                        actual: value.actual!
                    });
                }
            }
        }

        return {
            warn: Array.from(warns.values()),
            avoid: Array.from(avoids.values())
        };
    }

    // Generates a warning segment
    // Returns the segment (if any) and the index for the start of the new warning segment (if any)
    private _generateWarningSegment(actualValue: number, warningPoints: WarningPoint[], pointWarn: WeatherWarning|undefined, warnStartIdx: number,
                                    currIndex: number): { newWarnStartIdx: number, newSegment: WarningSegment|null }
    {
        let newWarnStartIdx = -1;
    
        let newSegment: WarningSegment = {
            actual:      actualValue,
            coordinates: [warningPoints[warnStartIdx].coordinates, warningPoints[currIndex-1].coordinates]
        };

        if (pointWarn !== undefined)
        {
            if (currIndex == warningPoints.length - 1)
            {
                // Last point; make sure we give a segment to the larger actual, even if it is the last point
                newSegment.actual = Math.max(actualValue, pointWarn.actual!);
                newSegment.coordinates[1] = warningPoints[currIndex].coordinates;
            }
            else
            {
                if (pointWarn.actual! < actualValue)
                {
                    // Current value is bigger than next segment, so prioritise this segment
                    newSegment.coordinates[1] = warningPoints[currIndex].coordinates;
                    newWarnStartIdx = currIndex;
                }
                else
                {
                    // next value is bigger than this segment, so prioritise next segment
                    newWarnStartIdx = currIndex - 1;
                }
            }
        }
    
        // Ignore segment if it is empty (start coordinates == end coordinates)
        if (newSegment.coordinates[0][0] === newSegment.coordinates[1][0] && newSegment.coordinates[0][1] === newSegment.coordinates[1][1])
        {
            return { newWarnStartIdx, newSegment: null };
        }
    
        return { newWarnStartIdx, newSegment };
    }
}
